import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useValuations } from '../../../hooks/administration/useValuations';
import formatDate from './utils/formatDate';

const schema = yup.object({
  proid: yup
    .number()
    .typeError('Debe seleccionar un proyecto.')
    .required('El proyecto es obligatorio.'),
  monid: yup
    .number()
    .typeError('Debe seleccionar una moneda.')
    .required('La moneda es obligatoria.'),
  tipimpid: yup
    .number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null || originalValue === undefined
        ? null
        : value
    ),
  valcabfecini: yup
    .string()
    .required('La fecha de inicio es obligatoria.'),
  valcabfecfin: yup
    .string()
    .required('La fecha de fin es obligatoria.')
    .test(
      'is-after-start',
      'La fecha fin no puede ser menor que la fecha inicio.',
      function (value) {
        const { valcabfecini } = this.parent;
        if (!valcabfecini || !value) return true;
        return value >= valcabfecini;
      }
    ),
  valcabdes: yup
    .string()
    .nullable()
    .max(1000, 'La descripción no debe superar los 1000 caracteres.'),
  valcabest: yup
    .string()
    .oneOf(['CREADA', 'EN_PROCESO'], 'El estado inicial debe ser CREADA o EN_PROCESO.')
    .required('El estado inicial es obligatorio.'),
  search: yup
    .string()
    .nullable()
    .max(150, 'La búsqueda no debe superar los 150 caracteres.'),
});

const EMPTY_FORM = {
  proid: '',
  monid: '',
  tipimpid: '',
  valcabfecini: '',
  valcabfecfin: '',
  valcabdes: '',
  valcabest: 'CREADA',
  search: '',
};

const INFO_STATE = {
  CREADA: {
    label: 'CREADA',
    color: '#90a4ae',
    text: '#fff',
    description:
      'La valorización se generará y quedará editable para revisión, ajustes y validación posterior.',
  },
  EN_PROCESO: {
    label: 'EN PROCESO',
    color: '#1976d2',
    text: '#fff',
    description:
      'La valorización se generará y quedará marcada como documento en trabajo activo.',
  },
};

/*const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};*/

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return `S/ ${number.toFixed(2)}`;
};

const ValuationGenerateForm = ({
  open,
  handleClose,
  projects = [],
  currencies = [],
  taxes = [],
}) => {
  const {
    generateAutomaticMutation,
    useEligibleOrdersPreview,
  } = useValuations();

  const [previewEnabled, setPreviewEnabled] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  useEffect(() => {
    if (open) {
      reset(EMPTY_FORM);
      setPreviewEnabled(false);
    }
  }, [open, reset]);

  const watchedProject = watch('proid');
  const watchedCurrency = watch('monid');
  const watchedTax = watch('tipimpid');
  const watchedStart = watch('valcabfecini');
  const watchedEnd = watch('valcabfecfin');
  const watchedState = watch('valcabest');
  const watchedSearch = watch('search');

  const selectedProject = projects.find((p) => Number(p.proid) === Number(watchedProject));
  const selectedCurrency = currencies.find((m) => Number(m.monid) === Number(watchedCurrency));
  const selectedTax = taxes.find((t) => Number(t.tipimpid) === Number(watchedTax));
  const stateInfo = INFO_STATE[watchedState] || INFO_STATE.CREADA;

  const previewQuery = useEligibleOrdersPreview({
    proid: watchedProject,
    fecini: watchedStart,
    fecfin: watchedEnd,
    search: watchedSearch || '',
    limit: 1000,
    offset: 0,
    enabled: open && previewEnabled,
  });

  const previewData = previewQuery.data || {
    totalOrdenes: 0,
    totalMonto: 0,
    grupos: [],
  };

  const canPreview = useMemo(() => {
    return Boolean(watchedProject && watchedStart && watchedEnd);
  }, [watchedProject, watchedStart, watchedEnd]);

  const canGenerate = useMemo(() => {
    return (
      Boolean(watchedProject && watchedCurrency && watchedStart && watchedEnd) &&
      !generateAutomaticMutation.isPending &&
      !previewQuery.isFetching
    );
  }, [
    watchedProject,
    watchedCurrency,
    watchedStart,
    watchedEnd,
    generateAutomaticMutation.isPending,
    previewQuery.isFetching,
  ]);

  const handlePreview = async () => {
    const isValid = await trigger([
      'proid',
      'valcabfecini',
      'valcabfecfin',
      'search',
    ]);

    if (!isValid) return;

    setPreviewEnabled(true);
  };

  const onSubmit = (formData) => {
    const payload = {
      proid: Number(formData.proid),
      monid: Number(formData.monid),
      tipimpid:
        formData.tipimpid === '' || formData.tipimpid === null
          ? null
          : Number(formData.tipimpid),
      valcabfecini: formData.valcabfecini,
      valcabfecfin: formData.valcabfecfin,
      valcabdes: formData.valcabdes?.trim() || null,
      valcabest: formData.valcabest,
      search: formData.search?.trim() || '',
    };

    generateAutomaticMutation.mutate(payload, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const noOrdersFound = previewEnabled && !previewQuery.isFetching && previewData.totalOrdenes === 0;

  return (
    <Dialog
      open={open}
      onClose={generateAutomaticMutation.isPending ? undefined : handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1.2}>
          <AutoAwesomeIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Generar valorización automática
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crea una valorización y asocia automáticamente las órdenes de servicio elegibles del periodo.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
        <Box component="form" id="valuation-generate-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2.5}>
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                Este proceso buscará órdenes de servicio en estado <strong>FINALIZADA</strong>,
                del <strong>proyecto</strong> y <strong>rango de fechas</strong> que indiques,
                y las agrupará automáticamente dentro de una nueva valorización.
              </Typography>
            </Alert>

            {/* Bloque 1 */}
            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                1. Contexto de la valorización
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Controller
                    name="proid"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Proyecto"
                        error={!!errors.proid}
                        helperText={
                          errors.proid?.message ||
                          'Las órdenes elegibles se buscarán dentro de este proyecto.'
                        }
                      >
                        <MenuItem value="">
                          <em>Seleccionar proyecto</em>
                        </MenuItem>
                        {projects.map((project) => (
                          <MenuItem key={project.proid} value={project.proid}>
                            {project.pronom}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="monid"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Moneda"
                        error={!!errors.monid}
                        helperText={
                          errors.monid?.message ||
                          'Moneda del documento generado.'
                        }
                      >
                        <MenuItem value="">
                          <em>Seleccionar moneda</em>
                        </MenuItem>
                        {currencies.map((currency) => (
                          <MenuItem key={currency.monid} value={currency.monid}>
                            {currency.moncod} - {currency.monnom}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="tipimpid"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Impuesto"
                        error={!!errors.tipimpid}
                        helperText={
                          errors.tipimpid?.message ||
                          'Opcional. Se aplicará al total calculado.'
                        }
                      >
                        <MenuItem value="">
                          <em>Sin impuesto</em>
                        </MenuItem>
                        {taxes.map((tax) => (
                          <MenuItem key={tax.tipimpid} value={tax.tipimpid}>
                            {tax.tipimpnom || tax.tipimpdes || `Impuesto ${tax.tipimpid}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>

              {(selectedProject || selectedCurrency || selectedTax) && (
                <Box mt={2}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedProject && (
                      <Chip
                        label={`Proyecto: ${selectedProject.pronom}`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {selectedCurrency && (
                      <Chip
                        label={`Moneda: ${selectedCurrency.moncod}`}
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {selectedTax && (
                      <Chip
                        label={`Impuesto: ${selectedTax.tipimpnom || selectedTax.tipimpdes || selectedTax.tipimpid}`}
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Paper>

            {/* Bloque 2 */}
            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                2. Rango y criterio de búsqueda
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="valcabfecini"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        fullWidth
                        size="small"
                        label="Fecha inicio"
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.valcabfecini}
                        helperText={
                          errors.valcabfecini?.message ||
                          'Desde esta fecha se buscarán OS finalizadas.'
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="valcabfecfin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        fullWidth
                        size="small"
                        label="Fecha fin"
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.valcabfecfin}
                        helperText={
                          errors.valcabfecfin?.message ||
                          'Hasta esta fecha se buscarán OS finalizadas.'
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="search"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Filtro adicional (opcional)"
                        placeholder="Nombre, tipo de servicio o texto relacionado"
                        error={!!errors.search}
                        helperText={
                          errors.search?.message ||
                          'Usa este campo si quieres reducir las OS detectadas.'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {(watchedStart || watchedEnd) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Se buscarán OS del periodo <strong>{watchedStart || '---'}</strong> a{' '}
                    <strong>{watchedEnd || '---'}</strong>.
                  </Typography>
                </Alert>
              )}
            </Paper>

            {/* Bloque 3 */}
            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                3. Estado inicial y descripción
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="valcabest"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Estado inicial"
                        error={!!errors.valcabest}
                        helperText={
                          errors.valcabest?.message ||
                          'Solo se permite iniciar en CREADA o EN_PROCESO.'
                        }
                      >
                        <MenuItem value="CREADA">CREADA</MenuItem>
                        <MenuItem value="EN_PROCESO">EN_PROCESO</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Controller
                    name="valcabdes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={3}
                        label="Descripción"
                        placeholder="Ejemplo: Valorización automática correspondiente a servicios del periodo..."
                        error={!!errors.valcabdes}
                        helperText={
                          errors.valcabdes?.message ||
                          'Recomendado para identificar claramente el documento generado.'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box mt={2}>
                <Chip
                  label={stateInfo.label}
                  sx={{
                    backgroundColor: stateInfo.color,
                    color: stateInfo.text,
                    fontWeight: 'bold',
                  }}
                />
                <Typography variant="body2" color="text.secondary" mt={1.2}>
                  {stateInfo.description}
                </Typography>
              </Box>
            </Paper>

            {/* Bloque 4: Preview */}
            <Paper
              variant="outlined"
              sx={{
                p: 2.2,
                borderRadius: 2.5,
                backgroundColor: '#fff',
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={2}
                mb={2}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    4. Previsualización antes de generar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Revisa cuántas órdenes serían incluidas antes de crear la valorización.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={
                    previewQuery.isFetching ? (
                      <CircularProgress size={18} />
                    ) : (
                      <PreviewIcon />
                    )
                  }
                  onClick={handlePreview}
                  disabled={!canPreview || previewQuery.isFetching}
                  sx={{ textTransform: 'none' }}
                >
                  {previewQuery.isFetching ? 'Analizando...' : 'Previsualizar'}
                </Button>
              </Box>

              {!previewEnabled && (
                <Alert severity="info" variant="outlined">
                  Completa proyecto y rango de fechas, luego usa <strong>Previsualizar</strong> para ver qué OS serán tomadas.
                </Alert>
              )}

              {previewEnabled && previewQuery.isFetching && (
                <Box display="flex" justifyContent="center" py={4}>
                  <Stack alignItems="center" spacing={1.5}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Buscando órdenes elegibles...
                    </Typography>
                  </Stack>
                </Box>
              )}

              {previewEnabled && previewQuery.isError && (
                <Alert severity="error">
                  {previewQuery.error?.message || 'Error al obtener la previsualización.'}
                </Alert>
              )}

              {previewEnabled && !previewQuery.isFetching && !previewQuery.isError && (
                <>
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={4}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2, height: '100%' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Órdenes detectadas
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" mt={1}>
                          {previewData.totalOrdenes || 0}
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2, height: '100%' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Monto estimado base
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" mt={1}>
                          {formatCurrency(previewData.totalMonto || 0)}
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2, height: '100%' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Grupos por tipo
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" mt={1}>
                          {previewData.grupos?.length || 0}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {noOrdersFound ? (
                    <Alert severity="warning">
                      No se encontraron órdenes elegibles para los criterios indicados.
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {previewData.grupos?.map((grupo) => (
                        <Accordion key={grupo.serProID} disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              width="100%"
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={1}
                            >
                              <Box>
                                <Typography fontWeight="bold">
                                  {grupo.serProNom}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Unidad: {grupo.serProUni || '-'}
                                </Typography>
                              </Box>

                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  size="small"
                                  label={`${grupo.cantidadOS} OS`}
                                  color="primary"
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  label={formatCurrency(grupo.subtotal)}
                                  color="success"
                                  variant="outlined"
                                />
                              </Stack>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                              <Stack spacing={1}>
                                {grupo.ordenes?.map((orden) => (
                                  <Paper
                                    key={orden.ordsercabid}
                                    variant="outlined"
                                    sx={{ p: 1.5, borderRadius: 2 }}
                                  >
                                    <Grid container spacing={1}>
                                      <Grid item xs={12} md={2}>
                                        <Typography variant="body2" color="text.secondary">
                                          Código
                                        </Typography>
                                        <Typography fontWeight="bold" color="primary">
                                          OS-{String(orden.ordsercabid).padStart(6, '0')}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" color="text.secondary">
                                          Nombre
                                        </Typography>
                                        <Typography>
                                          {orden.ordsercabnom}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} md={2}>
                                        <Typography variant="body2" color="text.secondary">
                                          Fecha
                                        </Typography>
                                        <Typography>
                                          {formatDate(orden.ordsercabfec)}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} md={2}>
                                        <Typography variant="body2" color="text.secondary">
                                          Estado
                                        </Typography>
                                        <Typography>
                                          {orden.ordsercabest}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} md={2}>
                                        <Typography variant="body2" color="text.secondary">
                                          Precio
                                        </Typography>
                                        <Typography fontWeight="bold">
                                          {formatCurrency(orden.ordsercabpre)}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Paper>

            {/* Bloque 5: resumen final */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderStyle: 'dashed',
                backgroundColor: '#fff',
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Resumen de generación
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Proyecto
                  </Typography>
                  <Typography fontWeight="medium">
                    {selectedProject?.pronom || 'No seleccionado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Moneda
                  </Typography>
                  <Typography fontWeight="medium">
                    {selectedCurrency
                      ? `${selectedCurrency.moncod} - ${selectedCurrency.monnom}`
                      : 'No seleccionada'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Impuesto
                  </Typography>
                  <Typography fontWeight="medium">
                    {selectedTax?.tipimpnom || selectedTax?.tipimpdes || 'Sin impuesto'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Periodo
                  </Typography>
                  <Typography fontWeight="medium">
                    {watchedStart || '---'} a {watchedEnd || '---'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Estado inicial
                  </Typography>
                  <Typography fontWeight="medium">
                    {stateInfo.label}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {(generateAutomaticMutation.isError) && (
              <Alert severity="error">
                {generateAutomaticMutation.error?.message ||
                  'Ocurrió un error al generar la valorización.'}
              </Alert>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={generateAutomaticMutation.isPending}
          startIcon={<CloseIcon />}
        >
          Cancelar
        </Button>

        <Button
          variant="outlined"
          onClick={handlePreview}
          disabled={!canPreview || previewQuery.isFetching || generateAutomaticMutation.isPending}
          startIcon={
            previewQuery.isFetching ? <CircularProgress size={18} /> : <SearchIcon />
          }
        >
          {previewQuery.isFetching ? 'Analizando...' : 'Previsualizar'}
        </Button>

        <Button
          type="submit"
          form="valuation-generate-form"
          variant="contained"
          disabled={!canGenerate || noOrdersFound}
          startIcon={
            generateAutomaticMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {generateAutomaticMutation.isPending ? 'Generando...' : 'Generar valorización'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValuationGenerateForm;